import React from "react";
import { Plus, Trash2, Lock, History } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { fmtINR } from "../../lib/api";

/**
 * Phase 211 — unified demand-lines editor for Demand Upload → Third Party → Manual Entry.
 * Row shape: { uid, demand_id?, financial_year_id, tax_head_id, arrear_amount, current_amount,
 *              paid_amount?, balance_amount?, payment_status?, fy_label?, tax_head_name?, correction_count?,
 *              orig: {arrear_amount, current_amount} (existing rows only) }
 *  - Existing rows (demand_id set): FY + Tax Head are locked (identity of the demand); amounts editable →
 *    saved as a Demand Correction with a mandatory reason. They cannot be removed here.
 *  - New rows: everything editable; blank rows are ignored on submit.
 */
let _uid = 0;
export const newLine = (patch = {}) => ({ uid: `n${++_uid}`, demand_id: null, financial_year_id: "", tax_head_id: "", arrear_amount: "", current_amount: "", ...patch });

export const fromLookupDemand = (d) => ({
  uid: d.demand_id, demand_id: d.demand_id, financial_year_id: d.financial_year_id || "", tax_head_id: d.tax_head_id || "",
  arrear_amount: String(d.arrear_amount ?? 0), current_amount: String(d.current_amount ?? 0),
  paid_amount: d.paid_amount || 0, balance_amount: d.balance_amount || 0, payment_status: d.payment_status,
  penalty_amount: d.penalty_amount || 0, rebate_amount: d.rebate_amount || 0,
  fy_label: d.fy_label, tax_head_name: d.tax_head_name, tax_code: d.tax_code, correction_count: d.correction_count || 0,
  source: d.source, head_active: d.head_active !== false,
  orig: { arrear_amount: Number(d.arrear_amount || 0), current_amount: Number(d.current_amount || 0) },
});

const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
export const lineTotal = (l) => Math.round((num(l.arrear_amount) + num(l.current_amount)) * 100) / 100;
export const lineHasAmount = (l) => lineTotal(l) > 0;
export const lineChanged = (l) => !!l.demand_id && l.orig &&
  (Math.abs(num(l.arrear_amount) - l.orig.arrear_amount) >= 0.005 || Math.abs(num(l.current_amount) - l.orig.current_amount) >= 0.005);

/** Rows → API payload (blank new rows dropped). */
export const linesPayload = (lines) => (lines || [])
  .filter((l) => l.demand_id || lineHasAmount(l) || l.financial_year_id || l.tax_head_id)
  .map((l) => ({ demand_id: l.demand_id || null, financial_year_id: l.financial_year_id || null, tax_head_id: l.tax_head_id || null,
    arrear_amount: num(l.arrear_amount), current_amount: num(l.current_amount) }));

/** Client-side validation → error string or null. */
export const validateLines = (lines) => {
  const seen = new Set();
  for (let i = 0; i < (lines || []).length; i++) {
    const l = lines[i];
    const n = i + 1;
    if (num(l.arrear_amount) < 0 || num(l.current_amount) < 0) return `Demand line ${n}: amounts cannot be negative`;
    if (!l.demand_id) {
      if (!lineHasAmount(l) && !l.financial_year_id && !l.tax_head_id) continue;   // blank row
      if (!lineHasAmount(l)) return `Demand line ${n}: enter an arrear or current amount (or remove the line)`;
      if (!l.financial_year_id) return `Demand line ${n}: select the Financial Year`;
      if (!l.tax_head_id) return `Demand line ${n}: select the Tax Head`;
    }
    const key = `${l.financial_year_id}|${l.tax_head_id}`;
    if (l.financial_year_id && l.tax_head_id) {
      if (seen.has(key)) return `Demand line ${n}: the same Financial Year + Tax Head appears twice — merge the amounts into one line`;
      seen.add(key);
    }
  }
  return null;
};

const STATUS = {
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  partial: "bg-amber-50 text-amber-700 border-amber-200",
  pending: "bg-slate-100 text-slate-700 border-slate-200",
};

export function TPDemandLinesEditor({ lines, onChange, fys = [], taxHeads = [], disabled = false, testIdPrefix = "tp-lines", autoHint = null }) {
  const update = (uid, patch) => onChange(lines.map((l) => (l.uid === uid ? { ...l, ...patch } : l)));
  const remove = (uid) => onChange(lines.filter((l) => l.uid !== uid));
  const add = () => onChange([...lines, newLine()]);
  const existing = lines.filter((l) => l.demand_id);
  const changedCount = lines.filter(lineChanged).length;
  const totals = lines.reduce((a, l) => ({
    demand: a.demand + lineTotal(l), paid: a.paid + num(l.paid_amount),
    balance: a.balance + (l.demand_id ? (lineChanged(l) ? Math.max(lineTotal(l) + num(l.penalty_amount) - num(l.rebate_amount) - num(l.paid_amount), 0) : num(l.balance_amount)) : lineTotal(l)),
  }), { demand: 0, paid: 0, balance: 0 });
  const headLabel = (id) => { const h = taxHeads.find((x) => x.id === id); return h ? `${h.tax_head_name}${h.tax_code ? ` (${h.tax_code})` : ""}` : null; };
  const fyLabel = (id) => fys.find((f) => f.id === id)?.fy_label || null;

  return (
    <div className="space-y-2 border rounded-lg p-3 bg-muted/30" data-testid={`${testIdPrefix}-editor`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label className="flex items-center gap-1.5">Demand lines
          {existing.length > 0 && <Badge variant="outline" className="text-[10px] font-normal" data-testid={`${testIdPrefix}-existing-count`}>{existing.length} existing</Badge>}
          {changedCount > 0 && <Badge variant="outline" className="text-[10px] font-normal bg-amber-50 text-amber-800 border-amber-200" data-testid={`${testIdPrefix}-changed-count`}>{changedCount} changed → correction</Badge>}
        </Label>
        <Button type="button" variant="outline" size="sm" onClick={add} disabled={disabled} data-testid={`${testIdPrefix}-add`}>
          <Plus className="h-4 w-4 mr-1.5" /> Add line
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground">
        One line per Financial Year + Tax Head. Leave every line blank to save the property only.
        {existing.length > 0 && <> Existing lines keep their FY and Tax Head; change the amounts and give a reason below — the ledger is rebuilt (Demand Corrections).</>}
        {autoHint && <span className="block mt-0.5 text-sky-700" data-testid={`${testIdPrefix}-autocalc-hint`}>{autoHint}</span>}
      </p>
      <div className="overflow-x-auto -mx-1">
        <Table className="min-w-[720px]">
          <TableHeader>
            <TableRow>
              <TableHead className="h-8 text-xs w-[220px]">Tax Head</TableHead>
              <TableHead className="h-8 text-xs w-[140px]">Financial Year</TableHead>
              <TableHead className="h-8 text-xs text-right w-[120px]">Arrear (₹)</TableHead>
              <TableHead className="h-8 text-xs text-right w-[120px]">Current (₹)</TableHead>
              <TableHead className="h-8 text-xs text-right">Total</TableHead>
              <TableHead className="h-8 text-xs text-right">Paid</TableHead>
              <TableHead className="h-8 text-xs text-right">Balance</TableHead>
              <TableHead className="h-8 text-xs w-[40px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {lines.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-xs text-muted-foreground text-center py-4" data-testid={`${testIdPrefix}-empty`}>No demand lines — the property will be saved without a demand. Use “Add line” to raise one.</TableCell></TableRow>
            )}
            {lines.map((l, i) => {
              const locked = !!l.demand_id;
              const changed = lineChanged(l);
              const total = lineTotal(l);
              const balance = locked ? (changed ? Math.max(total + num(l.penalty_amount) - num(l.rebate_amount) - num(l.paid_amount), 0) : num(l.balance_amount)) : total;
              return (
                <TableRow key={l.uid} className={changed ? "bg-amber-50/60" : ""} data-testid={`${testIdPrefix}-row-${i}`} data-demand-id={l.demand_id || ""} data-changed={changed ? "1" : "0"}>
                  <TableCell className="py-1.5 align-top">
                    {locked ? (
                      <div className="text-xs">
                        <div className="flex items-center gap-1 font-medium"><Lock className="h-3 w-3 text-muted-foreground" /> {l.tax_head_name || headLabel(l.tax_head_id) || "—"}{l.tax_code ? <span className="text-muted-foreground"> ({l.tax_code})</span> : null}</div>
                        <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                          {l.payment_status && <Badge variant="outline" className={`text-[10px] font-normal capitalize ${STATUS[l.payment_status] || STATUS.pending}`} data-testid={`${testIdPrefix}-row-${i}-status`}>{l.payment_status}</Badge>}
                          {l.correction_count > 0 && <span className="text-[10px] text-muted-foreground flex items-center gap-0.5" title="Earlier corrections"><History className="h-3 w-3" /> {l.correction_count}</span>}
                          {!l.head_active && <span className="text-[10px] text-amber-700">inactive head</span>}
                        </div>
                      </div>
                    ) : (
                      <Select value={l.tax_head_id} onValueChange={(v) => update(l.uid, { tax_head_id: v })} disabled={disabled}>
                        <SelectTrigger className="h-9 text-xs" data-testid={`${testIdPrefix}-row-${i}-head`}><SelectValue placeholder="Select tax head" /></SelectTrigger>
                        <SelectContent>
                          {taxHeads.map((h) => <SelectItem key={h.id} value={h.id}>{h.tax_head_name}{h.tax_code ? ` (${h.tax_code})` : ""}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  </TableCell>
                  <TableCell className="py-1.5 align-top">
                    {locked ? (
                      <span className="text-xs font-medium" data-testid={`${testIdPrefix}-row-${i}-fy-label`}>{l.fy_label || fyLabel(l.financial_year_id) || "—"}</span>
                    ) : (
                      <Select value={l.financial_year_id} onValueChange={(v) => update(l.uid, { financial_year_id: v })} disabled={disabled}>
                        <SelectTrigger className="h-9 text-xs" data-testid={`${testIdPrefix}-row-${i}-fy`}><SelectValue placeholder="Select FY" /></SelectTrigger>
                        <SelectContent>
                          {fys.map((f) => <SelectItem key={f.id} value={f.id}>{f.fy_label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  </TableCell>
                  <TableCell className="py-1.5 align-top">
                    <Input type="number" min="0" step="0.01" value={l.arrear_amount} onChange={(e) => update(l.uid, { arrear_amount: e.target.value })}
                      className="h-9 text-xs text-right tabular-nums" placeholder="0" disabled={disabled} data-testid={`${testIdPrefix}-row-${i}-arrear`} />
                    {changed && Math.abs(num(l.arrear_amount) - l.orig.arrear_amount) >= 0.005 && <p className="text-[10px] text-amber-800 text-right mt-0.5">was {fmtINR(l.orig.arrear_amount)}</p>}
                  </TableCell>
                  <TableCell className="py-1.5 align-top">
                    <Input type="number" min="0" step="0.01" value={l.current_amount} onChange={(e) => update(l.uid, { current_amount: e.target.value })}
                      className="h-9 text-xs text-right tabular-nums" placeholder="0" disabled={disabled} data-testid={`${testIdPrefix}-row-${i}-current`} />
                    {changed && Math.abs(num(l.current_amount) - l.orig.current_amount) >= 0.005 && <p className="text-[10px] text-amber-800 text-right mt-0.5">was {fmtINR(l.orig.current_amount)}</p>}
                  </TableCell>
                  <TableCell className="py-1.5 align-top text-right text-xs tabular-nums font-medium" data-testid={`${testIdPrefix}-row-${i}-total`}>{fmtINR(total)}</TableCell>
                  <TableCell className="py-1.5 align-top text-right text-xs tabular-nums text-muted-foreground" data-testid={`${testIdPrefix}-row-${i}-paid`}>{locked ? fmtINR(num(l.paid_amount)) : "—"}</TableCell>
                  <TableCell className="py-1.5 align-top text-right text-xs tabular-nums" data-testid={`${testIdPrefix}-row-${i}-balance`}>{fmtINR(balance)}</TableCell>
                  <TableCell className="py-1.5 align-top">
                    {!locked && (
                      <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => remove(l.uid)} disabled={disabled}
                        title="Remove line" data-testid={`${testIdPrefix}-row-${i}-remove`}><Trash2 className="h-4 w-4" /></Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {lines.length > 0 && (
        <div className="flex flex-wrap justify-end gap-x-4 gap-y-1 text-xs tabular-nums pt-1 border-t" data-testid={`${testIdPrefix}-totals`}>
          <span>Total demand <b>{fmtINR(totals.demand)}</b></span>
          {existing.length > 0 && <span className="text-muted-foreground">Paid <b className="text-foreground">{fmtINR(totals.paid)}</b></span>}
          <span>Balance <b>{fmtINR(totals.balance)}</b></span>
        </div>
      )}
    </div>
  );
}
